import type { AxiosResponse } from "axios";
import type { AgreementModel, AnalysisModel, CounterPartyModel, DocumentAuthorModel, ISingleReport, OverviewConfig, QuestionAndAnswerList, ReportDetailsList, SupportDocumentTypeList } from "../types/singleReport";
import { logger } from "./logger";



export function shouldShowOverviewBox(
  singleReportData: ISingleReport | undefined,
  overviewConfig: OverviewConfig | undefined
): boolean {
  if (
    overviewConfig?.isOverview === 1 &&
    (singleReportData?.reportOverview?.overview ||
      singleReportData?.reportOverview?.comment ||
      singleReportData?.reportOverview?.qualification)
  ) {
    return true;
  }

  const listsToCheck: Array<Array<SupportDocumentTypeList>> = [
    singleReportData?.mainDocTypeList ?? [],
    singleReportData?.supportDocTypeList ?? [],
  ];

  if (singleReportData?.reportDetailsList[0]?.hideDocuments === 0) {
      for (const list of listsToCheck) {
        for (const type of list) {
          const documents = singleReportData?.reportDetailsList?.filter(
            (document_) =>
              Number(document_.docTypeId) === Number(type.docTypeId) &&
            document_.displayInSource === 1
        );
        if (documents && documents.length > 0){
          return true;
        } 

      }
    }
  }

  if (
      overviewConfig?.isAnalysis === 1 &&
    singleReportData &&
   singleReportData?.reportAuthorAnalysis?.showAnalysisDate === 1 &&
   singleReportData?.reportAuthorAnalysis?.analysisDate 
  ) {
    return true;
  }

  return false;
}

interface JurisdictionData {
  opinionName: string;
  inProgressStatus: number;
  version: number;
  modifiedDate: string;
  jurisdictionId: number;
  status: number;
  opinionIDRef: string;
}
export const getUniqueJurisdictions = (reportDetailsList:Array<ReportDetailsList>, currentOpinionID?: string  ) => {
  const resultMap: Map<string, JurisdictionData> = new Map();
  reportDetailsList?.forEach((item) => {
    if (item.opinionId?.toString() === currentOpinionID?.toString()) {
      const key = `${item.opinionName}-${item.inProgressStatus}-${item.version}-${item.modifiedDate}-${item.jurisdictionId}-${item?.opinionIDRef}`;
      if (!resultMap.has(key)) {
        resultMap.set(key, {
          opinionName: item.opinionName,
          inProgressStatus: item.inProgressStatus,
          version: item.version,
          modifiedDate: item.modifiedDate,
          jurisdictionId: item.jurisdictionId,
          status: item.status,
          opinionIDRef: item.opinionIDRef,
        });
      }
    }
  });
  return [...resultMap.values()];
}

export const getAnalysis = (opinionID: string, data: Array<AnalysisModel>): Array<string> => {
  const filtered = data.filter(item => item.opinionId.toString() === opinionID.toString()).sort((a, b) =>
  a.analysisName?.localeCompare(b.analysisName)
);

  const uniqueAnalysisNames = [...new Set(filtered.map((item:AnalysisModel)=> item.analysisName))];

  return uniqueAnalysisNames;
};

export const getAgreement = (opinionID: string, data: Array<AgreementModel>): Array<string> => {
  const filtered = data.filter(item => item.opinionId.toString() === opinionID.toString()).sort((a, b) =>
  a.agreementName?.localeCompare(b.agreementName)
);

  const uniqueAgreementNames = [...new Set(filtered.map((item:AgreementModel)=> item.agreementName))];

  return uniqueAgreementNames;
};

export const getCounterparty = (opinionID: string, data: Array<CounterPartyModel>): Array<string> => {
  const filtered = data.filter(item => item.opinionId.toString() === opinionID.toString() && item.counterPartyName);


  const uniqueCounterPartyNames = [...new Set(filtered.map((item:CounterPartyModel)=> item.counterPartyName))];

  return uniqueCounterPartyNames;
};

export const getAddress = (opinionID: string, data: Array<DocumentAuthorModel>): Array<string> => {
  const filtered = data.filter(item => item.opinionId.toString() === opinionID.toString());

  const uniqueDocumentAddressees = [...new Set(filtered.map((item:DocumentAuthorModel)=> item.docAddressee ?? ''))];

  return uniqueDocumentAddressees;
};

export const getRecipientEmailListString = (opinionOrg?: { revDateAlertRecp?: string }): string => {
  const recipients = opinionOrg?.revDateAlertRecp?.trim();

  if (!recipients) {
    return "None";
  }

  // Clean and normalize whitespace and commas
  const formatted = recipients
    .split(",")
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
    .join(", ");

  return formatted || "None";
};

export function extractTemplateIds(rawIds: Array<string | undefined | null>): string {
  const regex = /T(\d+)/;
  const templateIdSet = new Set<string>();

  rawIds?.forEach((id) => {
    if (id?.trim()) {
      const match = regex.exec(id);
      if (match?.[1]) {
        templateIdSet.add(match[1]);
      }
    }
  });

  return [...templateIdSet].join(',');
}

export function extractIds(
  rawIds: Array<string> | undefined,
  regex: RegExp,
  asString = false,
  unique = false
): Array<string> | string {
  if (!rawIds?.length) return asString ? '' : [];

  let ids = rawIds
    .filter(id => id?.trim())
    .map(id => {
      const match = regex.exec(id);
      return match ? match[1] : null;
    })
    .filter(Boolean) as Array<string>;

  if (unique) {
    ids = [...new Set(ids)];
  }

  return asString ? ids.join(',') : ids;
}

export function getDistinctQuestions(questions:Array<QuestionAndAnswerList> = []) {
  if (!Array.isArray(questions) || questions.length === 0) {
    return [];
  }

  const distinct = [...new Map(
      questions.map(item => [`${item?.question?.questionId}_${item?.question?.questionTitle}`, item])
    ).values()];

  return distinct.sort((a, b) => (a.question.displayOrder ?? 0) - (b.question.displayOrder ?? 0));
}

export function isCompareDividerQuestion(
  questionBlock?: QuestionAndAnswerList
): boolean {
  if (!questionBlock?.question?.questionId) {
    return false;
  }

  const questionId = questionBlock.question.questionId;

  const dividerEntry =
    questionBlock.allDividerQuestionResponseTypeModels?.find(
      (item) =>
        item.questionId === questionId && item.responseTypeId === 15
    ) ??
    questionBlock.questionResponseTypeModelForDividerQuestions?.find(
      (item) =>
        item.questionId === questionId && item.responseTypeId === 15
    ) ??
    questionBlock.questionResponseTypeModelForDividerQuestions?.[0];

  return dividerEntry?.isDivider === 1 && dividerEntry?.responseTypeId === 15;
}

export const triggerFileDownload = (
  response: AxiosResponse<Blob>,
  fallbackFilename: string = "download"
) => {
  const blob = response.data;

  // Parse Content-Disposition header for filename
  const cd =
    response.headers["content-disposition"] ??
    response.headers["Content-Disposition"] ??
    "";
  const filenameMatch = cd.match(
    /filename\*=utf-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i
  );
  const downloadName =
    (filenameMatch &&
      decodeURIComponent(filenameMatch[1] ?? filenameMatch[2] ?? filenameMatch[3])) ??
    fallbackFilename;

  // Trigger browser download
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = downloadName;
  document.body.append(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export function formatToMidnightDate(input: string | Date | null | undefined): string {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0'); 
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day} 00:00:00`;
}

// utils/getSetPos.ts
export const getSetPos = (): void => {
  // Select all div elements with id starting with "aoTemplateH_"
  const templateHeaders = document.querySelectorAll<HTMLDivElement>('div[id^="aoTemplateH_"]');

  if (templateHeaders.length === 0) return;

  const fixMainHead = document.querySelector<HTMLElement>('.ao_fixMainHeadJS');
  const topHeader = document.querySelector<HTMLElement>('.ao_header > .navbar');
  const fullscreenIcon = document.querySelector<HTMLElement>('.ao_fullscreenIcon');
  const fixSubHead = document.querySelector<HTMLElement>('.ao_fixSubHeadJS');
  const gridScroller = document.querySelector<HTMLElement>('.ao_fullScreen .ao_gridScroller');
  const templateCloneGrid = document.querySelector<HTMLElement>('#ao_templateClone > .ao_grid');

  const fixMainHeadHeight = fixMainHead?.offsetHeight || 0;
  const topHeaderHeight = topHeader?.offsetHeight || 0;
  const fullscreenIconHeight = fullscreenIcon?.offsetHeight || 0;

  // Helper function to find position of an element
  const findPosition = (object: HTMLElement | null): number => {
    if (!object) return 0;
    let currentTop = 0;
    let element: HTMLElement | null = object;
    while (element) {
      currentTop += element.offsetTop;
      element = element.offsetParent as HTMLElement | null;
    }
    return currentTop;
  };

  // Helper function to handle dropdown change (same logic as AnswerContainerTableHeader)
  const changeTemplateValue = (value: string) => {
    const target = document.querySelector<HTMLElement>(`#${value}`);
    if (!target) return;

    if (document.querySelector('.ao_fullScreen')) {
      const element = document.querySelector<HTMLElement>(
        '#ao_fullScreenScroll'
      );
      element?.scrollTo(0, findPosition(target) - 160);
    } else {
      window.scrollTo(0, findPosition(target) - 210);
    }
  };

  const firstTemplate = templateHeaders[0];
  const getPosFirst =
    firstTemplate.getBoundingClientRect().top -
    fixMainHeadHeight -
    topHeaderHeight;

  templateHeaders.forEach((template) => {
    const rect = template.getBoundingClientRect();
    const getPos =
      rect.top -
      fixMainHeadHeight -
      topHeaderHeight -
      fullscreenIconHeight;

    if (getPos < 0) {
      // Get the selected value from the original dropdown before cloning
      const originalSelect = template.querySelector<HTMLSelectElement>('select[name="questionList"]');
      const selectedValue = originalSelect?.value || '';

      // Copy HTML content
      const getHtml = template.innerHTML;

      if (fixSubHead) {
        fixSubHead.style.display = 'block';
        fixSubHead.style.top = `${fixMainHeadHeight + topHeaderHeight + fullscreenIconHeight}px`;
        fixSubHead.style.visibility = 'visible';
      }

      if (gridScroller) {
        gridScroller.style.height = 'calc(100vh - 170px)';
      }

      if (templateCloneGrid) {
        templateCloneGrid.innerHTML = getHtml;
        
        // Reattach onChange handler to the cloned dropdown and preserve selected value
        // Since innerHTML replaces the element, we need to attach the handler each time
        const clonedSelect = templateCloneGrid.querySelector<HTMLSelectElement>('select[name="questionList"]');
        if (clonedSelect) {
          // Preserve the selected value from the original dropdown
          if (selectedValue) {
            clonedSelect.value = selectedValue;
          }
          
          // Attach the onChange handler
          clonedSelect.addEventListener('change', (event) => {
            const target = event.target as HTMLSelectElement;
            if (target.value) {
              changeTemplateValue(target.value);
              // Also update the original dropdown to keep them in sync
              if (originalSelect) {
                originalSelect.value = target.value;
              }
            }
          });
        }
      }
    }

    if (getPosFirst > 0) {
      if(fixSubHead) {
        fixSubHead.style.visibility = 'hidden';
        fixSubHead.style.display = 'none';
      }
      if(gridScroller) {
        gridScroller.style.height = 'calc(100vh - 120px)';
      }
    }
  });
};


export const getMaxResponseDisplayOrder = (
  responses: Array<any>,
  questionId: number,
  templateId: number
): number => {
  const filtered = responses.filter(
    (item) =>
      item.questionId === questionId &&
      item.templateId === templateId
  );

  if (filtered.length === 0) return 0;

  return Math.max(
    ...filtered.map(item => item.responseDisplayOrder ?? 0)
  );
};
export const encodeXmlStringToBase64 = (xml: string | null | undefined): string | null | undefined => {
  if (!xml) {
    return xml;
  }

  try {
    if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
      // Encode as UTF-8 before base64 to avoid issues with non-ASCII characters
      const utf8Xml = encodeURIComponent(xml).replaceAll(
        /%([\dA-F]{2})/g,
        (_, hex) => String.fromCodePoint(Number.parseInt(hex, 16)),
      );
      return window.btoa(utf8Xml);
    }
  } catch (error) {
    logger.error('Failed to encode XML string to Base64 for Part 2 payload', error);
  }

  // Fallback: return original XML string if encoding is not available or fails
  return xml;
};